import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Accueil", path: "/" },
  { label: "À Propos", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Contact", path: "/contact" },
];

const MOBILE_MENU_ANIM = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.25 },
};

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [setIsScrolled]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[#1a2e48]/90 backdrop-blur-xl border-b border-white/5 shadow-2xl"
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="group" data-testid="logo-link">
          <img
            src="/images/logo-pierre-affretement.png"
            alt="Pierre Affrètement"
            className="w-auto object-contain group-hover:opacity-90 transition-opacity"
            style={{ mixBlendMode: "screen", height: "68px", marginTop: "4px" }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-").replace("à", "a")}`}
              className={`font-heading font-semibold text-sm tracking-widest uppercase transition-all duration-200 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#E8902A] after:transition-all after:duration-300 hover:after:w-full ${
                location.pathname === link.path
                  ? "text-[#E8902A] after:w-full"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/devis" data-testid="devis-header-btn">
            <button className="bg-[#E8902A] hover:bg-[#C26218] text-white font-heading font-bold text-sm tracking-widest uppercase px-6 py-3 transition-all duration-300 shadow-[0_4px_14px_0_rgba(232,144,42,0.35)] hover:shadow-[0_6px_20px_rgba(232,144,42,0.5)] hover:-translate-y-0.5">
              Devis Gratuit
            </button>
          </Link>
        </nav>

        <button
          className="md:hidden text-white p-2 hover:text-[#E8902A] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            {...MOBILE_MENU_ANIM}
            className="md:hidden bg-[#1a2e48]/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-heading font-bold text-sm tracking-widest uppercase py-1 transition-colors ${
                    location.pathname === link.path
                      ? "text-[#E8902A]"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/devis">
                <button className="w-full bg-[#E8902A] text-white font-heading font-bold text-sm tracking-widest uppercase px-6 py-3 mt-2 shadow-[0_4px_14px_0_rgba(232,144,42,0.35)]">
                  Devis Gratuit
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
